import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';

const PrivacyPolicy: React.FC = () => {
  const navigate = useNavigate();
  
  const handleBack = () => {
    // Go back if there's history, otherwise go home
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate('/');
    }
  };

  return (
    <div className="min-h-screen bg-black text-gray-100 selection:bg-cyan-500/30">
      {/* Background */}
      <div className="fixed inset-0 pointer-events-none z-0">
        <div className="absolute -top-40 -left-40 w-96 h-96 bg-purple-900/20 rounded-full blur-[100px]" />
        <div className="absolute -bottom-40 -right-40 w-96 h-96 bg-cyan-900/20 rounded-full blur-[100px]" />
      </div>

      <div className="relative z-10 max-w-4xl mx-auto px-6 py-12">
        {/* Back Button */}
        <button
          onClick={handleBack}
          className="flex items-center gap-2 text-zinc-400 hover:text-white transition-colors mb-8 group"
        >
          <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" />
          <span>Volver</span>
        </button>

        {/* Header */}
        <div className="mb-12">
          <h1 className="text-3xl md:text-4xl font-black text-white mb-4">
            Política de Privacidad
          </h1>
          <p className="text-zinc-500 text-sm">
            Última actualización: {new Date().toLocaleDateString('es-AR', { year: 'numeric', month: 'long', day: 'numeric' })}
          </p>
        </div>

        {/* Content */}
        <div className="prose prose-invert prose-zinc max-w-none space-y-8">
          
          <section className="bg-zinc-900/50 border border-zinc-800 rounded-2xl p-6 md:p-8">
            <h2 className="text-xl font-bold text-white mb-4">1. Responsable del Tratamiento</h2>
            <p className="text-zinc-300 leading-relaxed">
              <strong>InteligenciaLegal</strong> es el responsable del tratamiento de tus datos 
              personales recopilados a través de <strong>LegalPromptStudio</strong> (en adelante, 
              "la Plataforma").
            </p>
            <p className="text-zinc-300 leading-relaxed mt-4">
              Esta política se rige por la <strong>Ley N° 25.326 de Protección de Datos Personales</strong> 
              de la República Argentina y su Decreto Reglamentario N° 1558/2001, así como las 
              disposiciones de la <strong>Agencia de Acceso a la Información Pública (AAIP)</strong>.
            </p>
          </section>

          <section className="bg-zinc-900/50 border border-zinc-800 rounded-2xl p-6 md:p-8">
            <h2 className="text-xl font-bold text-white mb-4">2. Datos que Recopilamos</h2>
            <p className="text-zinc-300 leading-relaxed mb-4">
              Recopilamos los siguientes tipos de datos personales:
            </p>
            
            <div className="space-y-4">
              <div className="bg-zinc-800/50 rounded-xl p-4">
                <h3 className="text-white font-semibold mb-2">📋 Datos de Registro (vía Google)</h3>
                <ul className="list-disc list-inside text-zinc-400 space-y-1">
                  <li>Nombre completo</li>
                  <li>Dirección de correo electrónico</li>
                  <li>Foto de perfil</li>
                  <li>Identificador único de usuario</li>
                </ul>
              </div>
              
              <div className="bg-zinc-800/50 rounded-xl p-4">
                <h3 className="text-white font-semibold mb-2">💬 Datos de Uso</h3>
                <ul className="list-disc list-inside text-zinc-400 space-y-1">
                  <li>Prompts ingresados para análisis</li>
                  <li>Resultados de análisis generados</li>
                  <li>Historial de interacciones</li>
                  <li>Preferencias de país seleccionado</li>
                </ul>
              </div>
              
              <div className="bg-zinc-800/50 rounded-xl p-4">
                <h3 className="text-white font-semibold mb-2">🔧 Datos Técnicos</h3>
                <ul className="list-disc list-inside text-zinc-400 space-y-1">
                  <li>Dirección IP</li>
                  <li>Tipo de navegador y dispositivo</li>
                  <li>Fecha y hora de acceso</li>
                  <li>Cookies técnicas necesarias para el funcionamiento</li>
                </ul>
              </div>
            </div>
          </section>

          <section className="bg-zinc-900/50 border border-zinc-800 rounded-2xl p-6 md:p-8">
            <h2 className="text-xl font-bold text-white mb-4">3. Finalidad del Tratamiento</h2>
            <p className="text-zinc-300 leading-relaxed mb-4">
              Utilizamos tus datos personales para las siguientes finalidades:
            </p>
            <ul className="list-disc list-inside text-zinc-400 space-y-2">
              <li><strong>Prestación del servicio:</strong> Procesar y analizar tus prompts legales.</li>
              <li><strong>Personalización:</strong> Guardar tu historial y preferencias para mejorar tu experiencia.</li>
              <li><strong>Comunicación:</strong> Enviarte información relevante sobre el servicio (solo si lo autorizás).</li>
              <li><strong>Seguridad:</strong> Proteger la Plataforma contra usos indebidos y fraudulentos.</li>
              <li><strong>Mejora del servicio:</strong> Analizar el uso de la Plataforma de forma agregada y anónima.</li>
              <li><strong>Cumplimiento legal:</strong> Cumplir con obligaciones legales y requerimientos de autoridades.</li>
            </ul>
          </section>

          <section className="bg-zinc-900/50 border border-zinc-800 rounded-2xl p-6 md:p-8">
            <h2 className="text-xl font-bold text-white mb-4">4. Base Legal del Tratamiento</h2>
            <p className="text-zinc-300 leading-relaxed">
              Conforme a la Ley N° 25.326, el tratamiento de tus datos se basa en:
            </p>
            <ul className="list-disc list-inside text-zinc-400 mt-4 space-y-2">
              <li><strong>Tu consentimiento:</strong> Otorgado al registrarte y aceptar estos términos.</li>
              <li><strong>Ejecución de un contrato:</strong> Para prestarte el servicio solicitado.</li>
              <li><strong>Interés legítimo:</strong> Para mejorar y proteger la Plataforma.</li>
              <li><strong>Cumplimiento de obligaciones legales:</strong> Cuando la ley lo requiera.</li>
            </ul>
          </section>

          <section className="bg-zinc-900/50 border border-zinc-800 rounded-2xl p-6 md:p-8">
            <h2 className="text-xl font-bold text-white mb-4">5. Compartición de Datos</h2>
            <p className="text-zinc-300 leading-relaxed mb-4">
              Tus datos pueden ser compartidos con:
            </p>
            <ul className="list-disc list-inside text-zinc-400 space-y-2">
              <li>
                <strong>Google (Firebase):</strong> Para autenticación y almacenamiento seguro de datos.
                <a href="https://firebase.google.com/support/privacy" target="_blank" rel="noopener noreferrer" className="text-cyan-400 hover:text-cyan-300 ml-1">
                  Ver política de Firebase →
                </a>
              </li>
              <li>
                <strong>Google (Gemini AI):</strong> Para el procesamiento de prompts mediante inteligencia artificial.
                Los prompts son procesados de forma segura y no se utilizan para entrenar modelos de IA de Google.
              </li>
              <li>
                <strong>Autoridades competentes:</strong> Cuando exista una obligación legal o requerimiento judicial.
              </li>
            </ul>
            <div className="bg-emerald-950/30 border border-emerald-900/50 rounded-xl p-4 mt-4">
              <p className="text-emerald-200 font-medium">
                ✓ NO vendemos ni compartimos tus datos con terceros para fines publicitarios o de marketing.
              </p>
            </div>
          </section>

          <section className="bg-zinc-900/50 border border-zinc-800 rounded-2xl p-6 md:p-8">
            <h2 className="text-xl font-bold text-white mb-4">6. Transferencia Internacional de Datos</h2>
            <p className="text-zinc-300 leading-relaxed">
              Debido a que utilizamos servicios de Google (Firebase y Gemini), tus datos pueden 
              ser transferidos y procesados en servidores ubicados fuera de Argentina, incluyendo 
              Estados Unidos y otros países.
            </p>
            <p className="text-zinc-300 leading-relaxed mt-4">
              Google cumple con estándares de seguridad internacionales y cuenta con certificaciones 
              que garantizan un nivel adecuado de protección, conforme a lo establecido en el 
              artículo 12 de la Ley N° 25.326.
            </p>
          </section>

          <section className="bg-zinc-900/50 border border-zinc-800 rounded-2xl p-6 md:p-8">
            <h2 className="text-xl font-bold text-white mb-4">7. Tus Derechos (ARCO)</h2>
            <p className="text-zinc-300 leading-relaxed mb-4">
              Conforme a la Ley N° 25.326, tenés los siguientes derechos sobre tus datos personales:
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="bg-zinc-800/50 rounded-xl p-4">
                <h3 className="text-cyan-400 font-bold mb-2">🔍 Acceso</h3>
                <p className="text-zinc-400 text-sm">
                  Podés solicitar información sobre qué datos tenemos sobre vos.
                </p>
              </div>
              <div className="bg-zinc-800/50 rounded-xl p-4">
                <h3 className="text-cyan-400 font-bold mb-2">✏️ Rectificación</h3>
                <p className="text-zinc-400 text-sm">
                  Podés corregir datos inexactos o incompletos.
                </p>
              </div>
              <div className="bg-zinc-800/50 rounded-xl p-4">
                <h3 className="text-cyan-400 font-bold mb-2">🗑️ Supresión</h3>
                <p className="text-zinc-400 text-sm">
                  Podés solicitar la eliminación de tus datos cuando ya no sean necesarios.
                </p>
              </div>
              <div className="bg-zinc-800/50 rounded-xl p-4">
                <h3 className="text-cyan-400 font-bold mb-2">🚫 Oposición</h3>
                <p className="text-zinc-400 text-sm">
                  Podés oponerte al tratamiento de tus datos en ciertos casos.
                </p>
              </div>
            </div>
            <p className="text-zinc-300 leading-relaxed mt-4">
              Para ejercer estos derechos, contactanos a través de los medios indicados al final 
              de esta política. Responderemos tu solicitud en un plazo máximo de 10 días hábiles, 
              conforme a lo establecido en la Ley N° 25.326.
            </p>
          </section>

          <section className="bg-zinc-900/50 border border-zinc-800 rounded-2xl p-6 md:p-8">
            <h2 className="text-xl font-bold text-white mb-4">8. Cookies y Tecnologías Similares</h2>
            <p className="text-zinc-300 leading-relaxed">
              Utilizamos cookies técnicas esenciales para:
            </p>
            <ul className="list-disc list-inside text-zinc-400 mt-4 space-y-2">
              <li>Mantener tu sesión activa</li>
              <li>Recordar tus preferencias (como el país seleccionado)</li>
              <li>Garantizar la seguridad de la Plataforma</li>
            </ul>
            <p className="text-zinc-300 leading-relaxed mt-4">
              No utilizamos cookies de publicidad ni de seguimiento de terceros. Podés configurar 
              tu navegador para rechazar cookies, aunque esto puede afectar el funcionamiento 
              de la Plataforma.
            </p>
          </section>

          <section className="bg-zinc-900/50 border border-zinc-800 rounded-2xl p-6 md:p-8">
            <h2 className="text-xl font-bold text-white mb-4">9. Seguridad de los Datos</h2>
            <p className="text-zinc-300 leading-relaxed">
              Implementamos medidas de seguridad técnicas y organizativas para proteger tus datos, 
              incluyendo:
            </p>
            <ul className="list-disc list-inside text-zinc-400 mt-4 space-y-2">
              <li>Cifrado de datos en tránsito (HTTPS/TLS)</li>
              <li>Almacenamiento seguro en Firebase con reglas de acceso restrictivas</li>
              <li>Autenticación segura mediante OAuth 2.0 de Google</li>
              <li>Monitoreo de accesos y actividades sospechosas</li>
              <li>Límites de uso para prevenir abusos</li>
            </ul>
          </section>

          <section className="bg-zinc-900/50 border border-zinc-800 rounded-2xl p-6 md:p-8">
            <h2 className="text-xl font-bold text-white mb-4">10. Conservación de Datos</h2>
            <p className="text-zinc-300 leading-relaxed">
              Conservamos tus datos personales mientras:
            </p>
            <ul className="list-disc list-inside text-zinc-400 mt-4 space-y-2">
              <li>Tu cuenta permanezca activa.</li>
              <li>Sea necesario para cumplir con las finalidades descritas.</li>
              <li>Existan obligaciones legales de conservación.</li>
            </ul>
            <p className="text-zinc-300 leading-relaxed mt-4">
              Podés solicitar la eliminación de tu cuenta y datos en cualquier momento. El historial 
              de análisis puede ser eliminado directamente desde la Plataforma.
            </p>
          </section>

          <section className="bg-zinc-900/50 border border-zinc-800 rounded-2xl p-6 md:p-8">
            <h2 className="text-xl font-bold text-white mb-4">11. Menores de Edad</h2>
            <p className="text-zinc-300 leading-relaxed">
              La Plataforma está dirigida a usuarios mayores de 18 años. No recopilamos 
              intencionalmente datos de menores. Si sos padre, madre o tutor y creés que un 
              menor nos ha proporcionado datos, contactanos para que podamos eliminarlos.
            </p>
          </section>

          <section className="bg-zinc-900/50 border border-zinc-800 rounded-2xl p-6 md:p-8">
            <h2 className="text-xl font-bold text-white mb-4">12. Cambios en la Política</h2>
            <p className="text-zinc-300 leading-relaxed">
              Podemos actualizar esta Política de Privacidad ocasionalmente. Los cambios serán 
              efectivos desde su publicación en la Plataforma. Te recomendamos revisar esta 
              página periódicamente. Si los cambios son significativos, te notificaremos a 
              través de la Plataforma o por correo electrónico.
            </p>
          </section>

          <section className="bg-zinc-900/50 border border-zinc-800 rounded-2xl p-6 md:p-8">
            <h2 className="text-xl font-bold text-white mb-4">13. Autoridad de Control</h2>
            <p className="text-zinc-300 leading-relaxed">
              Si considerás que el tratamiento de tus datos no cumple con la normativa vigente, 
              tenés derecho a presentar una reclamación ante la <strong>Agencia de Acceso a la 
              Información Pública (AAIP)</strong>:
            </p>
            <div className="bg-zinc-800/50 rounded-xl p-4 mt-4">
              <ul className="list-none text-zinc-400 space-y-1">
                <li>📍 Av. Pte. Julio A. Roca 710, Piso 2° - CABA</li>
                <li>🌐 <a href="https://www.argentina.gob.ar/aaip" target="_blank" rel="noopener noreferrer" className="text-cyan-400 hover:text-cyan-300">www.argentina.gob.ar/aaip</a></li>
              </ul>
            </div>
          </section>

          <section className="bg-zinc-900/50 border border-zinc-800 rounded-2xl p-6 md:p-8">
            <h2 className="text-xl font-bold text-white mb-4">14. Contacto</h2>
            <p className="text-zinc-300 leading-relaxed">
              Para cualquier consulta relacionada con esta Política de Privacidad o el ejercicio 
              de tus derechos, podés contactarnos:
            </p>
            <ul className="list-none text-zinc-400 mt-4 space-y-2">
              <li>🌐 Web: <a href="https://inteligencialegal.com.ar" target="_blank" rel="noopener noreferrer" className="text-cyan-400 hover:text-cyan-300 transition-colors">inteligencialegal.com.ar</a></li>
              <li>📸 Instagram: <a href="https://instagram.com/inteligencialegal.ok" target="_blank" rel="noopener noreferrer" className="text-cyan-400 hover:text-cyan-300 transition-colors">@inteligencialegal.ok</a></li>
            </ul>
          </section>

        </div>

        {/* Footer */}
        <div className="mt-12 pt-8 border-t border-zinc-800 text-center">
          <p className="text-zinc-600 text-sm">
            © {new Date().getFullYear()} InteligenciaLegal. Todos los derechos reservados.
          </p>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
